import { Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import * as Tabs from "@radix-ui/react-tabs";
import { OrganizationResponseForAdmins } from "client/openapi";
import { AttentionIssue } from "client/openapi/models/AttentionIssue";
import { OnboardingStep } from "client/openapi/models/OnboardingStep";
import type { PeopleSearchResponse } from "client/openapi/models/PeopleSearchResponse";
import { PeopleTab } from "client/openapi/models/PeopleTab";
import type { PersonRow } from "client/openapi/models/PersonRow";
import type { TaskDue } from "client/openapi/models/TaskDue";
import { PeopleService } from "client/openapi/services/PeopleService";
import { useCallback, useEffect, useRef, useState } from "react";
import Notifications from "util/notifications";
import pluralize from "util/pluralize";
import InviteMenu from "./InviteMenu";
import MessageAll from "./MessageAll";
import { STEP_LABELS } from "./OnboardingSteps";
import Pager from "./Pager";
import PersonTableRow, { ISSUE_LABELS } from "./PersonTableRow";
import { TaskTableRow } from "./TasksDue";
import { dismissIssues, dismissTasks, personKey } from "./person";
import "./index.css";

const PAGE_SIZE = 10;

const TABS = [
  { value: PeopleTab.ATTENTION, label: "Needs attention" },
  { value: PeopleTab.ONBOARDING, label: "Onboarding" },
  { value: PeopleTab.ALL, label: "All" },
  { value: PeopleTab.STUDENT, label: "Students" },
  { value: PeopleTab.PARENT, label: "Parents" },
  { value: PeopleTab.TUTOR, label: "Tutors" },
];

const NOUNS: Record<PeopleTab, [string, string]> = {
  [PeopleTab.ATTENTION]: ["person", "people"],
  [PeopleTab.ONBOARDING]: ["person", "people"],
  [PeopleTab.ALL]: ["person", "people"],
  [PeopleTab.STUDENT]: ["student", "students"],
  [PeopleTab.PARENT]: ["parent", "parents"],
  [PeopleTab.TUTOR]: ["tutor", "tutors"],
};

/** "768 people", "5 tutors", "2 students match" */
function describeTotal(total: number, tab: PeopleTab, searching: boolean) {
  const [one, many] = NOUNS[tab];
  const people = `${total} ${pluralize(one, many, total)}`;
  return searching
    ? `${people} ${pluralize("matches", "match", total)}`
    : people;
}

/** The page shown, with the tab, search and filters it was loaded for. */
type List = PeopleSearchResponse & {
  tab: PeopleTab;
  q?: string;
  missing: OnboardingStep[];
  issues: AttentionIssue[];
};

/** A Needs attention task row's key; people's rows have personKey's. */
function taskKey(task: TaskDue): string {
  return `task-${task.id}`;
}

/**
 * A person's row without the tasks taken out (as on the server, a dismissed
 * task leaves every row it flagged); no row if that leaves it no issue.
 */
function withoutTasks(
  row: PersonRow,
  out: (key: string) => boolean
): PersonRow[] {
  const tasks = row.tasks?.filter((task) => !out(taskKey(task)));
  if (tasks?.length === row.tasks?.length) return [row];
  const issues = tasks?.length
    ? row.issues
    : row.issues.filter((issue) => issue !== AttentionIssue.TASK_DUE);
  return issues.length > 0 ? [{ ...row, tasks, issues }] : [];
}

/**
 * The list without these rows (each key is a tab and a row's key, as
 * "attention|task-7"; a task's key takes the task off people's rows too),
 * and its total and tab count without them. On Onboarding, the steps they
 * were missing count them no more; on Needs attention, nor do the issues a
 * row no longer has (a row that left: all of them; a task row: its task due).
 */
function withoutRows(list: List, keys: string[]): List {
  const out = (key: string) => keys.includes(`${list.tab}|${key}`);
  const results = list.results.flatMap((row) =>
    out(personKey(row)) ? [] : withoutTasks(row, out)
  );
  const tasks = list.tasks?.filter((task) => !out(taskKey(task)));
  const gone =
    list.results.length -
    results.length +
    (list.tasks?.length ?? 0) -
    (tasks?.length ?? 0);
  const missingCounts = { ...list.missing_counts };
  const issueCounts = { ...list.issue_counts };
  if (list.tab === PeopleTab.ONBOARDING) {
    list.results
      .filter((row) => out(personKey(row)))
      .forEach((row) =>
        Object.entries(row.onboarding?.steps ?? {}).forEach(([step, done]) => {
          if (!done) missingCounts[step] -= 1;
        })
      );
  }
  if (list.tab === PeopleTab.ATTENTION) {
    // Compared by key: a row that lost a task is a new copy of it.
    const kept = new Map(results.map((row) => [personKey(row), row.issues]));
    list.results.forEach((row) => {
      const issues = kept.get(personKey(row)) ?? [];
      row.issues
        .filter((issue) => !issues.includes(issue))
        .forEach((issue) => {
          issueCounts[issue] -= 1;
        });
    });
    issueCounts[AttentionIssue.TASK_DUE] -=
      (list.tasks?.length ?? 0) - (tasks?.length ?? 0);
  }
  return {
    ...list,
    results,
    tasks,
    total: list.total - gone,
    counts: { ...list.counts, [list.tab]: list.counts[list.tab] - gone },
    missing_counts: missingCounts,
    issue_counts: issueCounts,
  };
}

/**
 * A tab's filter pills: each reason someone can be in the tab for
 * (Onboarding: a step they are missing; Needs attention: an issue), with how
 * many in the whole tab (or search). Every pill always shows, and picking
 * pills changes no pill's number. Picked ones narrow the list to those with
 * all of them, and the label says how many that is (everyone in the tab when
 * none is).
 */
function FilterPills<T extends string>({
  label,
  labels,
  counts,
  total,
  most,
  picked,
  onChange,
}: {
  label: string;
  /** Each pill's name, in the order they show. */
  labels: Record<T, string>;
  counts: Record<string, number>;
  /** How many people the picked pills match. */
  total: number;
  /**
   * The most total can be (everyone in the tab): its room is kept, so the
   * pills stay put as total changes.
   */
  most: number;
  picked: T[];
  onChange: (picked: T[]) => void;
}) {
  return (
    <div className="people-missing" role="group" aria-label={label}>
      <span className="people-missing__label">
        {label}{" "}
        <span
          className="people-missing__total"
          style={{ minWidth: `${String(most).length + 2}ch` }}
        >
          ({total})
        </span>
      </span>
      {(Object.keys(labels) as T[]).map((value) => {
        const on = picked.includes(value);
        return (
          <button
            key={value}
            type="button"
            aria-pressed={on}
            className={`people-missing__chip${
              on ? " people-missing__chip--on" : ""
            }`}
            onClick={() =>
              onChange(
                on ? picked.filter((v) => v !== value) : [...picked, value]
              )
            }
          >
            {labels[value]}
            <span className="people-missing__count">{counts[value] ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function PeoplePanel({
  organization,
  refreshKey,
  onMeetingsChange,
}: {
  organization: OrganizationResponseForAdmins;
  refreshKey?: number;
  /** Something changed from a row's windows (a meeting, for the calendar). */
  onMeetingsChange?: () => void;
}) {
  const orgId = organization.id;
  const [input, setInput] = useState("");
  const [q, setQ] = useState<string>();
  const [tab, setTab] = useState(PeopleTab.ATTENTION);
  // The Onboarding tab's "Show people missing:" filter.
  const [missing, setMissing] = useState<OnboardingStep[]>([]);
  // The Needs attention tab's "Show people with:" filter.
  const [issues, setIssues] = useState<AttentionIssue[]>([]);
  const [backToAttention, setBackToAttention] = useState(false);
  const [loaded, setLoaded] = useState<List>();
  // Rows being dismissed (see withoutRows): out of the list and its counts
  // until the dismiss is saved (then out of the loaded page for good) or
  // fails (then back where they were).
  const [dismissing, setDismissing] = useState<string[]>([]);
  // A dismiss was sent since the page last loaded quietly.
  const [settling, setSettling] = useState(false);
  const list = loaded && withoutRows(loaded, dismissing);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloads, setReloads] = useState(0);
  const latestRequest = useRef(0);
  // Each change of tab, search or filter starts on page 1 (reset while
  // rendering, so the old page is never loaded for the new list).
  const listKey = `${tab}|${q ?? ""}|${missing.join(",")}|${issues.join(",")}`;
  const [page, goToPage] = useState(0);
  const [pageListKey, setPageListKey] = useState(listKey);
  if (pageListKey !== listKey) {
    setPageListKey(listKey);
    goToPage(0);
  }

  const refetch = () => setReloads((n) => n + 1);

  // The search box drives q 250 ms after the last keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQ(input.trim() || undefined), 250);
    return () => clearTimeout(timer);
  }, [input]);

  // Loads the current page; only the newest request is applied. A quiet load
  // doesn't dim the list. A page left empty (its last row dismissed) moves to
  // the last page there is.
  const load = useCallback(
    async (quiet = false) => {
      const request = ++latestRequest.current;
      if (!quiet) setLoading(true);
      // Each tab's filter applies on that tab only.
      const filters = {
        missing: tab === PeopleTab.ONBOARDING ? missing : [],
        issues: tab === PeopleTab.ATTENTION ? issues : [],
      };
      try {
        const result = await PeopleService.searchPeople({
          orgId,
          requestBody: {
            q,
            tab,
            ...filters,
            skip: page * PAGE_SIZE,
            limit: PAGE_SIZE,
          },
        });
        if (request !== latestRequest.current) return;
        // Needs attention's task rows count as rows of the page too.
        const shown = result.results.length + (result.tasks?.length ?? 0);
        if (shown === 0 && page > 0 && result.total > 0) {
          goToPage(Math.ceil(result.total / PAGE_SIZE) - 1);
          return;
        }
        setLoaded({ ...result, tab, q, ...filters });
        setFailed(false);
      } catch {
        if (request !== latestRequest.current) return;
        setFailed(true);
      }
      setLoading(false);
    },
    [orgId, q, tab, page, missing, issues]
  );

  useEffect(() => {
    load();
  }, [load, refreshKey, reloads]);

  // Once every dismiss sent is settled (saved or not), the page loads again
  // quietly: the next rows move up into it, the counts come from the server,
  // and a load that read the list before a dismiss was saved is thrown away.
  useEffect(() => {
    if (!settling || dismissing.length > 0) return;
    setSettling(false);
    load(true);
  }, [settling, dismissing, load]);

  // Typing on Needs attention moves to All, and clearing the search moves
  // back. Both apply at once (not 250 ms later) so the list and the tab
  // change together.
  function changeSearch(value: string) {
    setInput(value);
    if (!value.trim()) {
      setQ(undefined);
      if (backToAttention) setTab(PeopleTab.ATTENTION);
      setBackToAttention(false);
    } else if (!q && tab === PeopleTab.ATTENTION) {
      setQ(value.trim());
      setTab(PeopleTab.ALL);
      setBackToAttention(true);
    }
  }

  function changeTab(value: PeopleTab) {
    setTab(value);
    setMissing([]);
    setIssues([]);
    setBackToAttention(false);
  }

  // Takes a tab's rows out of the list at once (see withoutRows), while
  // their dismiss is sent. The rest of the page stays as it is until it
  // loads again quietly.
  function dismissRow(
    tab: PeopleTab,
    keys: string[],
    request: Promise<unknown>
  ) {
    const dismissed = keys.map((key) => `${tab}|${key}`);
    setSettling(true);
    setDismissing((others) => [...others, ...dismissed]);
    request
      .then(() => setLoaded((shown) => shown && withoutRows(shown, dismissed)))
      .catch(() => Notifications.error("Could not dismiss. Try again."))
      .finally(() =>
        setDismissing((others) =>
          others.filter((other) => !dismissed.includes(other))
        )
      );
  }

  // Dismisses each of the row's issues as shown (a task due: each of its
  // tasks, which leave every row they flagged).
  function dismiss(row: PersonRow) {
    dismissRow(
      PeopleTab.ATTENTION,
      [personKey(row), ...(row.tasks ?? []).map(taskKey)],
      dismissIssues(orgId, row)
    );
  }

  // Hides a task's own row (a task about no one).
  function dismissTask(task: TaskDue) {
    dismissRow(
      PeopleTab.ATTENTION,
      [taskKey(task)],
      dismissTasks(orgId, [task])
    );
  }

  // Takes the person out of Onboarding for good.
  function dismissOnboarding(row: PersonRow) {
    dismissRow(
      PeopleTab.ONBOARDING,
      [personKey(row)],
      PeopleService.dismissOnboarding({
        orgId,
        requestBody: { kind: row.kind, id: row.id },
      })
    );
  }

  return (
    <div className="py-6 px-7 rounded-xl bg-neutral-50">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold">People</h2>

        <div className="flex items-center gap-2">
          <div className="people-panel__search">
            <MagnifyingGlassIcon className="people-panel__search-icon" />
            <input
              id="people-panel-search"
              type="text"
              className="input m-0 py-1.5 pl-9 pr-8"
              placeholder="Search people by name, email or phone"
              aria-label="Search people by name, email or phone"
              maxLength={100}
              autoComplete="off"
              value={input}
              onChange={(e) => changeSearch(e.target.value)}
            />
            {input && (
              <button
                type="button"
                className="people-panel__clear"
                aria-label="Clear search"
                onClick={() => changeSearch("")}
              >
                <Cross2Icon />
              </button>
            )}
          </div>
          <InviteMenu orgId={orgId} refetch={refetch} />
        </div>
      </div>

      <Tabs.Root
        value={tab}
        onValueChange={(value) => changeTab(value as PeopleTab)}
      >
        <Tabs.List className="people-panel__tabs" aria-label="People">
          {TABS.map(({ value, label }) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className={`people-panel__tab${
                value === PeopleTab.ONBOARDING
                  ? " people-panel__tab--onboarding"
                  : ""
              }`}
            >
              {label}
              {list && (
                <span className="people-panel__count">
                  {list.counts[value]}
                </span>
              )}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs.Root>

      {/* Loading another page, search or filter of the tab shown fades its
          rows but not its filter pills (see index.css). */}
      <div
        className={`people-panel__body${
          !loading
            ? ""
            : list?.tab === tab
            ? " people-panel__body--filtering"
            : " people-panel__body--loading"
        }`}
      >
        {failed ? (
          <p className="people-panel__empty">
            People could not be loaded.{" "}
            <button
              type="button"
              className="people-panel__link"
              onClick={refetch}
            >
              Try again
            </button>
          </p>
        ) : list &&
          list.total === 0 &&
          list.missing.length === 0 &&
          list.issues.length === 0 ? (
          <p className="people-panel__empty">
            {list.tab === PeopleTab.ONBOARDING ? (
              list.q ? (
                `No one matching "${list.q}" is onboarding.`
              ) : (
                "No one is onboarding. People you invite, and families who send your inquiry form, show here."
              )
            ) : list.q ? (
              list.tab === PeopleTab.ATTENTION ? (
                `No one matching "${list.q}" needs attention.`
              ) : (
                `No ${NOUNS[list.tab][1]} match "${list.q}".`
              )
            ) : list.tab === PeopleTab.ATTENTION ? (
              <>
                Nothing needs your attention.{" "}
                <button
                  type="button"
                  className="people-panel__link"
                  onClick={() => changeTab(PeopleTab.ALL)}
                >
                  See everyone
                </button>
              </>
            ) : (
              `No ${NOUNS[list.tab][1]} yet.`
            )}
          </p>
        ) : (
          list && (
            <>
              {/* Pages on the left, right under the tabs; the count and
                  Message all (every row in the tab or search) on the right. */}
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {list.total > PAGE_SIZE && (
                  <Pager
                    page={page}
                    pageCount={Math.ceil(list.total / PAGE_SIZE)}
                    onPage={goToPage}
                    disabled={loading}
                  />
                )}
                <span className="ml-auto" />
                {list.tab !== PeopleTab.ATTENTION && (
                  <span className="text-sm text-neutral-600">
                    {describeTotal(list.total, list.tab, !!list.q)}
                  </span>
                )}
                <MessageAll
                  orgId={orgId}
                  tab={list.tab}
                  q={list.q}
                  missing={list.missing}
                  issues={list.issues}
                />
              </div>

              {list.tab === PeopleTab.ONBOARDING && (
                <FilterPills
                  label="Show people missing"
                  labels={STEP_LABELS}
                  counts={list.missing_counts ?? {}}
                  total={list.total}
                  most={list.counts[list.tab]}
                  picked={missing}
                  onChange={setMissing}
                />
              )}
              {list.tab === PeopleTab.ATTENTION && (
                <FilterPills
                  label="Show people with"
                  labels={ISSUE_LABELS}
                  counts={list.issue_counts ?? {}}
                  total={list.total}
                  most={list.counts[list.tab]}
                  picked={issues}
                  onChange={setIssues}
                />
              )}
              {list.total === 0 && (
                <p className="people-panel__empty">
                  {list.tab === PeopleTab.ATTENTION
                    ? "No one has all of those."
                    : "No one is missing all of those."}
                </p>
              )}

              {list.total > 0 && (
                <div className="people-panel__table-wrap">
                  <table className="people-panel__table">
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th>Type</th>
                        <th className="hidden lg:table-cell">Family</th>
                        {list.tab !== PeopleTab.ONBOARDING && (
                          <th className="hidden md:table-cell">Next meeting</th>
                        )}
                        <th>Status</th>
                        <th>
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.tab === PeopleTab.ATTENTION &&
                        (list.tasks ?? []).map((task) => (
                          <TaskTableRow
                            key={taskKey(task)}
                            task={task}
                            orgId={orgId}
                            onDismiss={() => dismissTask(task)}
                            onChange={refetch}
                          />
                        ))}
                      {list.results.map((row) => (
                        <PersonTableRow
                          key={personKey(row)}
                          row={row}
                          orgId={orgId}
                          onDismiss={
                            // Every Needs attention row is there for an issue
                            // (a meeting request and a task due are issues
                            // too), so every one can be dismissed.
                            list.tab === PeopleTab.ONBOARDING
                              ? () => dismissOnboarding(row)
                              : list.tab === PeopleTab.ATTENTION
                              ? () => dismiss(row)
                              : undefined
                          }
                          showOnboarding={list.tab === PeopleTab.ONBOARDING}
                          onChange={() => {
                            refetch();
                            onMeetingsChange?.();
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}
